import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Keyboard } from 'lucide-react';
import { CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useAppStore } from 'store';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { SCREEN_TYPES } from 'utils/constants';
import { startReceipt } from 'services/api/receipt';
import { useAuthStore } from 'store/authStore';
import { toast } from 'sonner';
import { useArrivalReceiptStore } from 'store/arrivalReceiptStore';
import { getFedexId } from 'services/barcodes/checkFedexId';

export default function Receiving() {
  const [trackingId, setTrackingId] = useState('');
  const [loading, setLoading] = useState(false);
  const trackingInputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(trackingInputRef);
  const { stationId, user } = useAuthStore();
  const { setCurrentArrivalReceipt } = useArrivalReceiptStore();
  const { setCurrentScreen, setBarcodeDebugMessage } = useAppStore();

  useEffect(() => {
    setTrackingId('');
  }, []);

  const handleSubmit = async (): Promise<void> => {
    let trimmed = trackingId.trim();
    if (!trimmed) {
      toast.warning('Please enter tracking number');
      return;
    }

    // Check for FedEx patterns
    const fedexId = getFedexId(trimmed);
    if (fedexId !== trimmed) {
      toast.success('✓ FedEx tracking detected! Processing...');
      setBarcodeDebugMessage(`FedEx ID detected: ${fedexId}`);
      trimmed = fedexId;
      setTrackingId(fedexId);
    }

    setLoading(true);
    try {
      const data = await startReceipt(trimmed, user?.id ?? '', stationId ?? '');
      if (!data || data.responseState === 'error') {
        toast.error(data?.responseMsg ?? 'Request failed');
      } else {
        toast.success(
          data.responseMsg === 'valid' ? '✓ Receipt arrived' : (data.responseMsg ?? '✓ Done')
        );
        setTrackingId('');
        setCurrentArrivalReceipt(data);
        if (data.responseState === 'problem' || data.responseState === 'exception') {
          setCurrentScreen(SCREEN_TYPES.RECEIVING_DISPOSITION);
        } else {
          requestAnimationFrame(() => trackingInputRef.current?.focus());
        }
      }
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900">
        Scan Shipping Box
      </CardTitle>

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label
          htmlFor="receiving-tracking"
          className="mb-2 block text-sm font-semibold text-slate-700"
        >
          Tracking #
        </Label>
        <div className="flex items-center gap-2.5">
          <Input
            ref={trackingInputRef}
            id="receiving-tracking"
            type="text"
            inputMode={keyboardVisible ? 'text' : 'none'}
            placeholder="Input field"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleSubmit();
              }
            }}
            onBlur={() => {
              if (!loading) requestAnimationFrame(() => trackingInputRef.current?.focus());
            }}
            disabled={loading}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            className="text-base"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
            onClick={toggleKeyboardInput}
            aria-pressed={keyboardVisible}
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="h-12 w-full rounded-xl text-base font-semibold"
        onClick={() => setCurrentScreen(SCREEN_TYPES.APP_SELECT)}
      >
        Back
      </Button>
    </div>
  );
}
